import React, { useState, useEffect } from "react";
import { Column, Bar } from "@ant-design/charts";
import { Card, Row, Col, Tag, Button } from "antd";
import moment from "moment";
import { getSlowOrders } from "../api/api";
import Spinner from "./Spinner";
import { Link } from "react-router-dom";
import { BarChartOutlined } from "@ant-design/icons";

export default function SlowGraph({ stageStats, statusStats, orders, selfSufficient }) {
  const [localData, setLocalData] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (selfSufficient) {
      setLocalLoading(true);
      getSlowOrders((mData) => {
        setLocalData(mData);
        setLocalLoading(false);
      });
    }
  }, [selfSufficient]);

  if (selfSufficient && localLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
        <Spinner />
      </div>
    );
  }

  // Resolve stage stats, status stats, and orders list
  const activeStageStats = selfSufficient ? localData?.stage_stats : stageStats;
  const activeStatusStats = selfSufficient ? localData?.status_stats : statusStats;

  let activeOrders = [];
  if (selfSufficient) {
    if (localData) {
      if (Array.isArray(localData)) {
        activeOrders = localData;
      } else if (localData.docs && Array.isArray(localData.docs)) {
        activeOrders = localData.docs;
      } else if (localData.orders && Array.isArray(localData.orders)) {
        activeOrders = localData.orders;
      } else if (localData.result && Array.isArray(localData.result)) {
        activeOrders = localData.result;
      }
    }
  } else {
    activeOrders = orders || [];
  }

  // Format stage stats data
  const stageData = [
    { stage: "Purchase", count: activeStageStats?.purchase || 0 },
    { stage: "Supplier", count: activeStageStats?.supplier || 0 },
    { stage: "China Warehouse", count: activeStageStats?.china_warehouse || 0 },
    { stage: "Shipment", count: activeStageStats?.shipment || 0 },
    { stage: "BD Warehouse", count: activeStageStats?.bd_warehouse || 0 },
  ];

  const stageConfig = {
    data: stageData,
    xField: "stage",
    yField: "count",
    seriesField: "stage",
    color: ["#1D3E71", "#4E287C", "#A83E28", "#125D66", "#276329"],
    label: {
      position: "middle",
      style: {
        fill: "#ffffff",
        opacity: 0.9,
        fontWeight: "bold",
      },
    },
    meta: {
      stage: { alias: "Stage" },
      count: { alias: "Orders" },
    },
  };

  // Format status stats data (only show statuses with count > 0)
  const statusData = (activeStatusStats || [])
    .filter((el) => el.count > 0)
    .map((el) => ({
      status: el.title,
      count: el.count,
    }));

  const statusConfig = {
    data: statusData,
    xField: "count",
    yField: "status",
    seriesField: "status",
    color: ["#1D3E71", "#4E287C", "#A83E28", "#125D66", "#276329", "#913E7C", "#7A4D1A", "#4A4A4A"],
    label: {
      position: "right",
      style: {
        fill: "#000000",
        opacity: 0.8,
      },
    },
    meta: {
      status: { alias: "Status" },
      count: { alias: "Orders" },
    },
  };

  // Format aging data
  const ageGroups = {
    "4-7 Days": 0,
    "7-15 Days": 0,
    "16-30 Days": 0,
    "31-60 Days": 0,
    "61-90 Days": 0,
    "91-180 Days": 0,
    "180+ Days": 0,
    "365+ Days": 0,
    "730+ Days": 0,
  };

  (activeOrders || []).forEach((order) => {
    const slowDays = order.day_count;

    if (slowDays >= 4) {
      if (slowDays <= 7) {
        ageGroups["4-7 Days"]++;
      } else if (slowDays <= 15) {
        ageGroups["7-15 Days"]++;
      } else if (slowDays <= 30) {
        ageGroups["16-30 Days"]++;
      } else if (slowDays <= 60) {
        ageGroups["31-60 Days"]++;
      } else if (slowDays <= 90) {
        ageGroups["61-90 Days"]++;
      } else if (slowDays <= 180) {
        ageGroups["91-180 Days"]++;
      } else if (slowDays <= 365) {
        ageGroups["180+ Days"]++;
      } else if (slowDays <= 730) {
        ageGroups["365+ Days"]++;
      } else {
        ageGroups["730+ Days"]++;
      }
    }
  });

  const agingData = Object.entries(ageGroups).map(([group, count]) => ({
    group,
    count,
  }));

  const agingConfig = {
    data: agingData,
    xField: "count",
    yField: "group",
    seriesField: "group",
    color: [
      "#276329", // 4-7 Days (Forest Green)
      "#3D7A25", // 7-15 Days (Green)
      "#579120", // 16-30 Days (Olive Green)
      "#7FA81B", // 31-60 Days (Lime Olive)
      "#A89D18", // 61-90 Days (Olive Yellow)
      "#B07E17", // 91-180 Days (Warm Amber)
      "#B55916", // 180+ Days (Orange)
      "#9E1616", // 365+ Days (Red)
      "#610B0B"  // 730+ Days (Deep Burgundy Red)
    ],
    label: {
      position: "right",
      style: {
        fill: "#000000",
        opacity: 0.8,
      },
    },
    meta: {
      group: { alias: "Age Group" },
      count: { alias: "Orders" },
    },
  };

  const renderContent = () => (
    <Row gutter={[16, 24]}>
      <Col span={24}>
        <Card title='Stage'>
          <div style={{ height: "330px" }}>
            <Column {...stageConfig} height={300} autoFit />
          </div>
        </Card>
      </Col>
      <Col span={24}>
        <Card title='Order Delay'>
          <div style={{ height: "350px" }}>
            <Bar {...agingConfig} height={330} autoFit />
          </div>
        </Card>
      </Col>
      <Col span={24}>
        <Card title='Status'>
          <div style={{ height: "680px" }}>
            {statusData.length > 0 ? (
              <Bar {...statusConfig} height={650} autoFit />
            ) : (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "640px", color: "gray" }}>
                No active status data
              </div>
            )}
          </div>
        </Card>
      </Col>
    </Row>
  );

  if (selfSufficient) {
    const displayCount = localData?.count !== undefined ? localData.count : (activeOrders?.length || 0);
    return (
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span>Slow Orders</span>
            {!localLoading && localData && (
              <Tag color="#D1412A" style={{ margin: 0, fontWeight: "bold" }}>
                {displayCount}
              </Tag>
            )}

          </div>
        }
        extra={
          <Link to="/report/advanced/slow-orders">
            <Button icon={<BarChartOutlined />}>
              View Orders
            </Button>
          </Link>
        }
        bordered={true}
        style={{ marginTop: "8px" }}
      >
        {renderContent()}
      </Card>
    );
  }

  return renderContent();
}
